import { AlertCircle, Briefcase, ChevronRight, CheckCircle2, Clock, Play } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { Job, JobStatus } from '@maker-toolchest/shared';
import { StatusBadge } from '@/components/StatusBadge';

interface JobSummaryPanelProps {
  jobs: Job[];
  isLoading?: boolean;
  error?: Error | null;
}

export function JobSummaryPanel({ jobs, isLoading, error }: JobSummaryPanelProps) {
  if (isLoading) {
    return (
      <div className="rounded border border-border bg-background-subtle p-4">
        <h2 className="mb-3 text-sm font-medium text-foreground">Jobs Summary</h2>
        <div className="flex items-center justify-center py-8">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded border border-border bg-background-subtle p-4">
        <h2 className="mb-3 text-sm font-medium text-foreground">Jobs Summary</h2>
        <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <AlertCircle className="mx-auto mb-2 h-8 w-8 text-red-500" />
            <p className="text-sm text-foreground">Failed to load jobs</p>
            <p className="text-xs text-foreground-muted mt-1">Please try again later</p>
          </div>
        </div>
      </div>
    );
  }

  // Calculate status counts
  const statusCounts = jobs.reduce(
    (acc, job) => {
      acc[job.status] = (acc[job.status] || 0) + 1;
      return acc;
    },
    {} as Record<JobStatus, number>
  );

  const totalJobs = jobs.length;
  const activeJobs = (statusCounts.IN_PROGRESS || 0) + (statusCounts.QUEUED || 0);
  const completedJobs = statusCounts.COMPLETED || 0;
  const pendingJobs = statusCounts.PENDING || 0;

  const statusCards = [
    {
      label: 'Active',
      count: activeJobs,
      icon: Play,
      color: 'text-info',
      bgColor: 'bg-info/10',
    },
    {
      label: 'Pending',
      count: pendingJobs,
      icon: Clock,
      color: 'text-warning',
      bgColor: 'bg-warning/10',
    },
    {
      label: 'Completed',
      count: completedJobs,
      icon: CheckCircle2,
      color: 'text-success',
      bgColor: 'bg-success/10',
    },
  ];

  // Get active jobs for quick view
  const activeJobsList = jobs
    .filter((job) => job.status === 'IN_PROGRESS' || job.status === 'QUEUED')
    .slice(0, 3);

  return (
    <div className="rounded border border-border bg-background-subtle p-4">
      <h2 className="mb-3 text-sm font-medium text-foreground">Jobs Summary</h2>

      {totalJobs === 0 ? (
        <div className="flex flex-col items-center justify-center py-6 text-center">
          <div className="mb-3 text-foreground-subtle">
            <Briefcase className="h-10 w-10" />
          </div>
          <h3 className="mb-1 text-sm font-medium text-foreground">No jobs yet</h3>
          <p className="mb-4 max-w-sm text-xs text-foreground-muted">Create your first job to track your printing tasks</p>
          <Link
            to="/projects/jobs"
            className="text-sm text-primary hover:underline"
          >
            Go to Jobs
          </Link>
        </div>
      ) : (
        <>
          {/* Status summary */}
          <div className="mb-4 grid grid-cols-3 gap-2">
            {statusCards.map((statusCard) => (
              <div
                key={statusCard.label}
                className={`rounded-lg border border-border p-3 ${statusCard.bgColor}`}
              >
                <div className="flex items-center gap-2">
                  <statusCard.icon className={`h-4 w-4 flex-shrink-0 ${statusCard.color}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-foreground-muted truncate">{statusCard.label}</div>
                    <div className={`mt-0.5 text-lg font-semibold ${statusCard.color}`}>
                      {statusCard.count}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Active jobs list */}
          {activeJobsList.length > 0 && (
            <>
              <h3 className="mb-2 text-xs font-medium text-foreground-muted uppercase">Active Jobs</h3>
              <div className="space-y-2 mb-3">
                {activeJobsList.map((job) => (
                  <Link
                    key={job.id}
                    to={`/projects/jobs/${job.id}`}
                    className="block rounded-lg border border-border bg-background p-3 hover:bg-background-subtle transition-colors"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-foreground truncate">
                          {job.name}
                        </div>
                        <div className="mt-1 flex items-center gap-2">
                          <StatusBadge status={job.status} size="sm" />
                          <span className="text-xs text-foreground-muted">
                            {Math.round(job.progress)}% complete
                          </span>
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 flex-shrink-0 text-foreground-muted" />
                    </div>
                  </Link>
                ))}
              </div>
            </>
          )}

          <div className="mt-3 flex justify-end">
            <Link
              to="/projects/jobs"
              className="flex items-center gap-1 text-sm text-primary hover:underline"
            >
              View all jobs
              <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
        </>
      )}
    </div>
  );
}
